/**
 * Inline editable overview fields — life summary, known for, featured memory.
 * Saves only user-entered profile data (no AI-generated text).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Pencil, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { humanizeError } from '../../utils/errorHandling';
import { getBiographicalNotesLabel, isCompanionProfile } from '../../utils/companionProfiles';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function parseLifeThemes(text) {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(0, 12);
}

const ALL_SECTIONS = ['summary', 'themes', 'featured'];

export function PersonOverviewRichFields({
  person,
  canEdit = false,
  onUpdated,
  featuredMemoryPreview = null,
  onOpenStories,
  sections = ALL_SECTIONS,
}) {
  const showSection = (key) => sections.includes(key);
  const [editingSummary, setEditingSummary] = useState(false);
  const [editingThemes, setEditingThemes] = useState(false);
  const [editingFeatured, setEditingFeatured] = useState(false);
  const [saving, setSaving] = useState(false);

  const [summaryDraft, setSummaryDraft] = useState('');
  const [themesDraft, setThemesDraft] = useState('');
  const [featuredDraft, setFeaturedDraft] = useState('');

  useEffect(() => {
    setSummaryDraft(person?.biographical_notes || '');
    setThemesDraft(Array.isArray(person?.life_themes) ? person.life_themes.join('\n') : '');
    setFeaturedDraft(person?.featured_memory_excerpt || '');
  }, [person?.id, person?.biographical_notes, person?.life_themes, person?.featured_memory_excerpt]);

  const lifeThemes = Array.isArray(person?.life_themes) ? person.life_themes.filter(Boolean) : [];
  const summaryLabel = isCompanionProfile(person) ? getBiographicalNotesLabel(person) : 'Life summary';

  const persist = async (patch) => {
    if (!person?.id) return;
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/individuals/${person.id}`, patch);
      onUpdated?.(data);
      toast.success('Saved.');
      return data;
    } catch (err) {
      toast.error(humanizeError(err));
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const saveSummary = async () => {
    await persist({ biographical_notes: summaryDraft.trim() || null });
    setEditingSummary(false);
  };

  const saveThemes = async () => {
    const life_themes = parseLifeThemes(themesDraft);
    await persist({ life_themes });
    setEditingThemes(false);
  };

  const saveFeatured = async () => {
    await persist({ featured_memory_excerpt: featuredDraft.trim() || null });
    setEditingFeatured(false);
  };

  const displayFeatured = featuredMemoryPreview?.text || null;

  return (
    <div className="space-y-7 min-w-0">
      {showSection('summary') ? (
      <section data-testid="overview-life-summary" className="min-w-0">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground/55">{summaryLabel}</p>
          {canEdit && !editingSummary ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 rounded-full text-muted-foreground"
              onClick={() => setEditingSummary(true)}
              data-testid="overview-edit-life-summary"
            >
              <Pencil className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              Edit
            </Button>
          ) : null}
        </div>
        {editingSummary ? (
          <div className="space-y-3 rounded-2xl border border-[hsl(38_14%_88%)]/55 bg-[hsl(42_28%_98.5%)]/90 px-5 py-5">
            <Label htmlFor="overview-life-summary-input" className="sr-only">
              {summaryLabel}
            </Label>
            <Textarea
              id="overview-life-summary-input"
              value={summaryDraft}
              onChange={(e) => setSummaryDraft(e.target.value)}
              rows={5}
              className="font-serif text-base leading-relaxed"
              placeholder="Who they were, where life took them, what stays with the family…"
            />
            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" className="rounded-full" disabled={saving} onClick={saveSummary}>
                <Save className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                Save
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="rounded-full"
                disabled={saving}
                onClick={() => {
                  setSummaryDraft(person?.biographical_notes || '');
                  setEditingSummary(false);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : person?.biographical_notes ? (
          <div className="rounded-2xl bg-[hsl(42_28%_98.5%)]/90 px-5 py-6 sm:px-8 sm:py-7 border border-[hsl(38_14%_88%)]/45 shadow-[inset_0_1px_0_0_hsla(0,0%,100%,0.65)]">
            <p className="font-serif text-[1.05rem] sm:text-lg leading-[1.65] text-foreground/88 max-w-prose whitespace-pre-wrap">
              {person.biographical_notes}
            </p>
          </div>
        ) : (
          <div className="rounded-2xl bg-muted/10 px-5 py-7 sm:px-8 border border-dashed border-[hsl(38_14%_82%)]/55">
            <p className="font-serif text-base leading-relaxed text-muted-foreground/85 max-w-prose">
              {canEdit
                ? 'A short written overview helps family browsing later — tap Edit to add one in their own words.'
                : 'No life summary has been added yet.'}
            </p>
          </div>
        )}
      </section>
      ) : null}

      {showSection('themes') ? (
      <section data-testid="overview-life-themes" aria-label="Known for">
        <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2">
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground/55">Known for</p>
          {canEdit && !editingThemes ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 rounded-full text-muted-foreground"
              onClick={() => setEditingThemes(true)}
              data-testid="overview-edit-life-themes"
            >
              <Pencil className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              Edit
            </Button>
          ) : null}
        </div>
        {editingThemes ? (
          <div className="space-y-3 rounded-2xl border border-[hsl(38_14%_88%)]/55 bg-[hsl(42_28%_98.5%)]/90 px-5 py-5">
            <Label htmlFor="overview-life-themes-input">One phrase per line</Label>
            <Textarea
              id="overview-life-themes-input"
              value={themesDraft}
              onChange={(e) => setThemesDraft(e.target.value)}
              rows={4}
              className="font-serif text-sm"
              placeholder={'Loved cooking\nSheffield roots\nFamily storyteller'}
            />
            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" className="rounded-full" disabled={saving} onClick={saveThemes}>
                Save
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="rounded-full"
                disabled={saving}
                onClick={() => {
                  setThemesDraft(Array.isArray(person?.life_themes) ? person.life_themes.join('\n') : '');
                  setEditingThemes(false);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : lifeThemes.length > 0 ? (
          <ul className="flex flex-wrap gap-2">
            {lifeThemes.map((t) => (
              <li key={t}>
                <span className="inline-block rounded-md border border-[hsl(38_14%_78%)]/35 bg-[hsl(40_18%_97%)]/80 px-2.5 py-1 text-[13px] font-serif text-muted-foreground/90">
                  {t}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="font-serif text-sm leading-relaxed text-muted-foreground/75 max-w-prose">
            {canEdit
              ? 'Add quiet phrases — traditions, crafts, humour, places — one per line when you edit.'
              : 'Nothing added yet.'}
          </p>
        )}
      </section>
      ) : null}

      {showSection('featured') ? (
      <section data-testid="overview-featured-memory" aria-label="Featured memory">
        <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2">
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground/55">Featured memory</p>
          {canEdit && !editingFeatured ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 rounded-full text-muted-foreground"
              onClick={() => setEditingFeatured(true)}
              data-testid="overview-edit-featured-memory"
            >
              <Pencil className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              Edit
            </Button>
          ) : null}
        </div>
        {editingFeatured ? (
          <div className="space-y-3 rounded-2xl border border-[hsl(38_14%_88%)]/55 bg-[hsl(42_28%_98.5%)]/90 px-5 py-5">
            <Label htmlFor="overview-featured-input">Curated snippet (optional)</Label>
            <Textarea
              id="overview-featured-input"
              value={featuredDraft}
              onChange={(e) => setFeaturedDraft(e.target.value)}
              rows={3}
              maxLength={400}
              className="font-serif text-sm"
              placeholder="Leave blank to pull a line from their first preserved story."
            />
            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" className="rounded-full" disabled={saving} onClick={saveFeatured}>
                Save
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="rounded-full"
                disabled={saving}
                onClick={() => {
                  setFeaturedDraft(person?.featured_memory_excerpt || '');
                  setEditingFeatured(false);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : displayFeatured ? (
          <blockquote className="border-l-[3px] border-[#b89a6a]/45 pl-5 sm:pl-6 py-0.5">
            <p className="font-serif text-base sm:text-[1.0625rem] leading-[1.65] text-foreground/85 italic">
              &ldquo;{displayFeatured}&rdquo;
            </p>
            {featuredMemoryPreview?.source === 'story' && typeof onOpenStories === 'function' ? (
              <footer className="mt-3">
                <button
                  type="button"
                  className="text-xs text-muted-foreground underline-offset-4 hover:underline hover:text-foreground/80"
                  onClick={onOpenStories}
                >
                  From a preserved story — open stories &amp; voice
                </button>
              </footer>
            ) : featuredMemoryPreview?.source === 'manual' ? (
              <p className="mt-3 text-[11px] text-muted-foreground/70 font-serif">Chosen for this profile</p>
            ) : null}
          </blockquote>
        ) : (
          <p className="font-serif text-sm leading-relaxed text-muted-foreground/75 max-w-prose pl-1">
            When stories carry a written or spoken memory, a line from the archive can appear here — or choose one with Edit.
          </p>
        )}
      </section>
      ) : null}
    </div>
  );
}

export default PersonOverviewRichFields;
